/**
 * QaNotesTab.tsx
 *
 * Staff-entered notes for a single QA case (patients_qa_notes), listed newest
 * to oldest. Notes support HTML bodies (TipTap) and file/image attachments,
 * which are stored on EFS under the patient's `qa` subfolder.
 */
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { cn, formatDate } from '@/lib/utils';
import { TiptapEditor } from '@/v5/components/ui/tiptap-editor';
import * as qaSvc from '@/services/qa.service';
import { Download, Paperclip, Trash2 } from 'lucide-react';

/**
 * Compact drag-and-drop attachment zone. Click to browse or drop files; the
 * selected files are handed to the parent (which owns the pending/draft list).
 */
function AttachDropzone({ onFiles }: { onFiles: (files: File[]) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  function emit(list: FileList | null) {
    const files = Array.from(list ?? []);
    if (files.length > 0) onFiles(files);
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
      }}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
      onDrop={(e) => { e.preventDefault(); setDragging(false); emit(e.dataTransfer.files); }}
      className={cn(
        'flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-3 text-sm transition-colors',
        dragging
          ? 'border-indigo-400 bg-indigo-50 text-indigo-600'
          : 'border-slate-300 bg-slate-50 text-slate-500 hover:border-slate-400 hover:bg-slate-100',
      )}
    >
      <Paperclip className="h-4 w-4" />
      <span>Drop files here or <span className="text-indigo-600">click to browse</span></span>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => { emit(e.target.files); if (inputRef.current) inputRef.current.value = ''; }}
      />
    </div>
  );
}

export function isEmptyHtml(html: string): boolean {
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
  return text === '';
}

/** Buffer for an initial note typed before the QA case exists (create flow). */
export interface QaNoteDraft {
  note: string;
  setNote: (html: string) => void;
  files: File[];
  setFiles: (files: File[]) => void;
}

interface QaNotesTabProps {
  /** Saved case id, or null while creating (use `draft`). */
  caseId: number | null;
  /** Required when `caseId` is null: buffers the initial note + attachments. */
  draft?: QaNoteDraft;
}

export function QaNotesTab({ caseId, draft }: QaNotesTabProps) {
  // Draft mode (no saved case yet): a single composer whose contents are
  // persisted by the parent and flushed once the case is created.
  if (caseId === null) {
    return draft ? <QaNotesDraftComposer draft={draft} /> : null;
  }
  return <QaNotesList caseId={caseId} />;
}

function QaNotesDraftComposer({ draft }: { draft: QaNoteDraft }) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        This note will be added to the case when you create it.
      </p>
      <div className="rounded-lg border border-slate-200">
        <TiptapEditor content={draft.note} onChange={draft.setNote} placeholder="Add an initial note..." />
        <div className="border-t border-slate-200 px-3 py-2">
          <AttachDropzone onFiles={(files) => draft.setFiles([...draft.files, ...files])} />
        </div>
        {draft.files.length > 0 && (
          <div className="flex flex-wrap gap-2 border-t border-slate-200 px-3 py-2">
            {draft.files.map((f, i) => (
              <span
                key={`${f.name}-${i}`}
                className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-1 text-xs text-slate-600"
              >
                {f.name}
                <button
                  type="button"
                  className="text-slate-400 hover:text-red-500"
                  onClick={() => draft.setFiles(draft.files.filter((_, j) => j !== i))}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function QaNotesList({ caseId }: { caseId: number }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [draft, setDraft] = useState('');
  const [editorKey, setEditorKey] = useState(0);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);

  const notesQuery = useQuery({
    queryKey: ['qa-notes', caseId],
    queryFn: () => qaSvc.listQaNotes(caseId),
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['qa-notes', caseId] });
  }

  async function handleAdd() {
    if (isEmptyHtml(draft) && pendingFiles.length === 0) return;
    setSaving(true);
    try {
      const { patientsQaNoteId } = await qaSvc.createQaNote(caseId, draft);
      if (pendingFiles.length > 0) {
        await Promise.all(
          pendingFiles.map((f) => qaSvc.uploadQaNoteAttachment(caseId, patientsQaNoteId, f)),
        );
      }
      setDraft('');
      setPendingFiles([]);
      setEditorKey((k) => k + 1);
      invalidate();
      toast.success('Note added.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to add note.');
    } finally {
      setSaving(false);
    }
  }

  const deleteNote = useMutation({
    mutationFn: (noteId: number) => qaSvc.deleteQaNote(caseId, noteId),
    onSuccess: () => {
      invalidate();
      toast.success('Note deleted.');
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to delete note.'),
  });

  const deleteAttachment = useMutation({
    mutationFn: (v: { noteId: number; attachmentId: number }) =>
      qaSvc.deleteQaNoteAttachment(caseId, v.noteId, v.attachmentId),
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to delete attachment.'),
  });

  async function handleAttachToNote(noteId: number, files: FileList | null) {
    if (!files || files.length === 0) return;
    try {
      await Promise.all(
        Array.from(files).map((f) => qaSvc.uploadQaNoteAttachment(caseId, noteId, f)),
      );
      invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to upload attachment.');
    }
  }

  const notes = notesQuery.data ?? [];

  return (
    <div className="space-y-5">
      {/* Composer */}
      <div className="rounded-lg border border-slate-200">
        <TiptapEditor
          key={editorKey}
          content={draft}
          onChange={setDraft}
          placeholder="Add a note..."
        />
        <div className="border-t border-slate-200 px-3 py-2">
          <AttachDropzone onFiles={(files) => setPendingFiles((prev) => [...prev, ...files])} />
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-3 py-2">
          <span className="text-xs text-slate-500">
            {pendingFiles.length > 0
              ? `${pendingFiles.length} file${pendingFiles.length === 1 ? '' : 's'} ready`
              : ''}
          </span>
          <Button onClick={handleAdd} disabled={saving} loading={saving} size="sm">
            Add Note
          </Button>
        </div>
        {pendingFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 border-t border-slate-200 px-3 py-2">
            {pendingFiles.map((f, i) => (
              <span
                key={`${f.name}-${i}`}
                className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-1 text-xs text-slate-600"
              >
                {f.name}
                <button
                  type="button"
                  className="text-slate-400 hover:text-red-500"
                  onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* List */}
      {notesQuery.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : notes.length === 0 ? (
        <p className="text-sm text-slate-500 italic">No notes yet.</p>
      ) : (
        <div className="space-y-3">
          {notes.map((n) => (
            <div key={n.patientsQaNoteId} className="rounded-lg border border-slate-200 bg-white">
              <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
                <div className="text-xs text-slate-500">
                  <span className="font-medium text-slate-700">{n.authorName || 'Unknown'}</span>
                  {' · '}
                  {formatDate(n.dateCreated)}
                </div>
                <button
                  type="button"
                  className="text-slate-400 hover:text-red-500"
                  title="Delete note"
                  onClick={() => deleteNote.mutate(n.patientsQaNoteId)}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div
                className="prose prose-sm max-w-none px-3 py-2"
                dangerouslySetInnerHTML={{ __html: n.note }}
              />
              <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-3 py-2">
                {n.attachments.map((a) => (
                  <span
                    key={a.attachmentId}
                    className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-1 text-xs text-slate-600"
                  >
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-indigo-600"
                      onClick={() =>
                        qaSvc.downloadQaNoteAttachment(
                          caseId,
                          n.patientsQaNoteId,
                          a.attachmentId,
                          a.filename,
                        )
                      }
                    >
                      <Download className="h-3 w-3" /> {a.filename}
                    </button>
                    <button
                      type="button"
                      className="text-slate-400 hover:text-red-500"
                      onClick={() =>
                        deleteAttachment.mutate({
                          noteId: n.patientsQaNoteId,
                          attachmentId: a.attachmentId,
                        })
                      }
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <label className="inline-flex cursor-pointer items-center gap-1 text-xs text-slate-500 hover:text-indigo-600">
                  <Paperclip className="h-3 w-3" /> Add file
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => handleAttachToNote(n.patientsQaNoteId, e.target.files)}
                  />
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default QaNotesTab;
